/**
 * Intelligent web crawler with natural language control
 */

import { EventEmitter } from 'node:events';
import axios from 'axios';
import { convertHtmlToMarkdown } from './article-converter.js';
import { ClaudeClient, type CrawlStrategy } from './claude-client.js';
import { fetchWithPlaywright, closeBrowser } from './playwright-crawler.js';
import { createLogger, summarizePayload } from '../logging/index.js';

const logger = createLogger('crawler');

/**
 * Get crawl strategy from Claude CLI without initializing lancedb.
 * Call this BEFORE createServices() to avoid fork-safety issues with lancedb.
 *
 * LanceDB's native Rust code is not fork-safe. If we spawn Claude CLI
 * after lancedb is loaded, the fork corrupts lancedb's mutex state.
 */
export async function getCrawlStrategy(
  seedUrl: string,
  crawlInstruction: string,
  useHeadless: boolean = true
): Promise<CrawlStrategy> {
  if (!ClaudeClient.isAvailable()) {
    throw new Error('Claude CLI not available: install Claude Code for intelligent crawling');
  }

  const client = new ClaudeClient();

  // Fetch seed HTML
  let seedHtml: string;
  if (useHeadless) {
    const headlessResult = await fetchWithPlaywright(seedUrl);
    seedHtml = headlessResult.html;
  } else {
    const response = await axios.get(seedUrl, {
      timeout: 30000,
      headers: {
        'User-Agent':
          'Mozilla/5.0 (compatible; BlueraKnowledge/1.0; +https://github.com/blueraai/bluera-knowledge)',
      },
    });
    seedHtml = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
  }

  // Get strategy from Claude
  return client.determineCrawlUrls(seedUrl, seedHtml, crawlInstruction);
}

export interface CrawlConfig {
  userAgent?: string; // User-Agent header for requests
  timeout?: number; // Per-page timeout in ms (default: 30000)
  maxConcurrency?: number; // Max concurrent requests (for future use)
}

export interface CrawlOptions {
  crawlInstruction?: string; // Natural language: what to crawl
  extractInstruction?: string; // Natural language: what to extract
  maxPages?: number; // Max pages to crawl (default: 50)
  timeout?: number; // Per-page timeout in ms (default: 30000)
  useHeadless?: boolean; // Enable headless browser for JavaScript-rendered sites
  preComputedStrategy?: CrawlStrategy; // Pre-computed strategy (to avoid fork issues with lancedb)
}

export interface CrawlResult {
  url: string;
  title?: string;
  markdown: string;
  extracted?: string;
  depth?: number;
}

export interface CrawlProgress {
  type: 'start' | 'strategy' | 'page' | 'extraction' | 'complete' | 'error';
  pagesVisited: number;
  totalPages: number;
  currentUrl?: string;
  message?: string;
  error?: Error;
}

// Default values for crawl config
const DEFAULT_USER_AGENT =
  'Mozilla/5.0 (compatible; BlueraKnowledge/1.0; +https://github.com/blueraai/bluera-knowledge)';
const DEFAULT_TIMEOUT = 30000;

/**
 * Intelligent crawler that uses Claude CLI for strategy and extraction
 */
export class IntelligentCrawler extends EventEmitter {
  private readonly claudeClient: ClaudeClient;
  private readonly visited: Set<string>;
  private readonly config: CrawlConfig;
  private stopped: boolean;

  constructor(config?: CrawlConfig) {
    super();
    this.claudeClient = new ClaudeClient();
    this.visited = new Set();
    this.config = config ?? {};
    this.stopped = false;
  }

  /**
   * Crawl a website using Claude-driven intelligent mode
   */
  async *crawl(seedUrl: string, options: CrawlOptions = {}): AsyncIterable<CrawlResult> {
    const {
      crawlInstruction = 'crawl all pages linked from this URL',
      extractInstruction,
      maxPages = 50,
    } = options;

    this.visited.clear();
    this.stopped = false;

    logger.info(
      {
        seedUrl,
        maxPages,
        mode: 'intelligent',
        hasExtractInstruction: extractInstruction !== undefined,
      },
      'Starting crawl'
    );

    const startProgress: CrawlProgress = {
      type: 'start',
      pagesVisited: 0,
      totalPages: maxPages,
    };
    this.emit('progress', startProgress);

    yield* this.crawlIntelligent(
      seedUrl,
      crawlInstruction,
      extractInstruction,
      maxPages,
      options.useHeadless ?? true,
      options.preComputedStrategy
    );

    logger.info(
      {
        seedUrl,
        pagesVisited: this.visited.size,
      },
      'Crawl complete'
    );

    // Warn if crawl discovered far fewer pages than requested
    if (this.visited.size === 1 && maxPages > 1) {
      const warningProgress: CrawlProgress = {
        type: 'error',
        pagesVisited: this.visited.size,
        totalPages: maxPages,
        message: `Warning: Only crawled 1 page despite maxPages=${String(maxPages)}. Link discovery may have failed. If using --fast mode, try without it for JavaScript-heavy sites.`,
        error: new Error('Low page discovery'),
      };
      this.emit('progress', warningProgress);
    }

    const completeProgress: CrawlProgress = {
      type: 'complete',
      pagesVisited: this.visited.size,
      totalPages: this.visited.size,
    };
    this.emit('progress', completeProgress);
  }

  /**
   * Intelligent mode: Use Claude to determine which URLs to crawl
   */
  private async *crawlIntelligent(
    seedUrl: string,
    crawlInstruction: string,
    extractInstruction: string | undefined,
    maxPages: number,
    useHeadless: boolean = true,
    preComputedStrategy?: CrawlStrategy
  ): AsyncIterable<CrawlResult> {
    let strategy: CrawlStrategy;

    // Use pre-computed strategy if provided (avoids fork issues with lancedb)
    if (preComputedStrategy !== undefined) {
      strategy = preComputedStrategy;
      const strategyProgress: CrawlProgress = {
        type: 'strategy',
        pagesVisited: 0,
        totalPages: maxPages,
        message: `Using pre-computed strategy: ${String(strategy.urls.length)} URLs to crawl`,
      };
      this.emit('progress', strategyProgress);
    } else {
      // Check if Claude CLI is available before attempting intelligent mode
      if (!ClaudeClient.isAvailable()) {
        throw new Error('Claude CLI not available: install Claude Code for intelligent crawling');
      }

      try {
        // Step 1: Fetch seed page HTML
        const strategyStartProgress: CrawlProgress = {
          type: 'strategy',
          pagesVisited: 0,
          totalPages: maxPages,
          currentUrl: seedUrl,
          message: 'Analyzing page structure with Claude...',
        };
        this.emit('progress', strategyStartProgress);

        const seedHtml = await this.fetchHtml(seedUrl, useHeadless);

        // Step 2: Ask Claude which URLs to crawl (pass seedUrl for relative URL resolution)
        strategy = await this.claudeClient.determineCrawlUrls(seedUrl, seedHtml, crawlInstruction);

        const strategyCompleteProgress: CrawlProgress = {
          type: 'strategy',
          pagesVisited: 0,
          totalPages: maxPages,
          message: `Claude identified ${String(strategy.urls.length)} URLs to crawl: ${strategy.reasoning}`,
        };
        this.emit('progress', strategyCompleteProgress);
      } catch (error) {
        // Re-throw strategy errors - do not fall back silently
        throw error instanceof Error ? error : new Error(String(error));
      }
    }

    // Step 3: Crawl each URL from Claude's strategy
    let pagesVisited = 0;

    for (const url of strategy.urls) {
      if (this.stopped || pagesVisited >= maxPages) break;
      if (this.visited.has(url)) continue;

      try {
        const result = await this.crawlSinglePage(
          url,
          extractInstruction,
          pagesVisited,
          useHeadless
        );
        pagesVisited++;
        yield result;
      } catch (error) {
        const pageErrorProgress: CrawlProgress = {
          type: 'error',
          pagesVisited,
          totalPages: maxPages,
          currentUrl: url,
          error: error instanceof Error ? error : new Error(String(error)),
        };
        this.emit('progress', pageErrorProgress);
      }
    }
  }

  /**
   * Crawl a single page: fetch, convert to markdown, optionally extract
   */
  private async crawlSinglePage(
    url: string,
    extractInstruction: string | undefined,
    pagesVisited: number,
    useHeadless: boolean = true
  ): Promise<CrawlResult> {
    const pageProgress: CrawlProgress = {
      type: 'page',
      pagesVisited,
      totalPages: 0,
      currentUrl: url,
    };
    this.emit('progress', pageProgress);

    // Mark as visited
    this.visited.add(url);

    // Fetch HTML
    const html = await this.fetchHtml(url, useHeadless);

    // Convert to clean markdown using slurp-ai techniques
    // Note: convertHtmlToMarkdown throws on errors, no need to check success
    const conversion = await convertHtmlToMarkdown(html, url);

    logger.debug(
      {
        url,
        title: conversion.title,
        markdownLength: conversion.markdown.length,
      },
      'Article converted to markdown'
    );

    let extracted: string | undefined;

    // Optional: Extract specific information using Claude
    if (extractInstruction !== undefined && extractInstruction !== '') {
      // Throw if extraction requested but Claude CLI isn't available
      if (!ClaudeClient.isAvailable()) {
        throw new Error('Claude CLI not available: install Claude Code for extraction');
      }

      const extractionProgress: CrawlProgress = {
        type: 'extraction',
        pagesVisited,
        totalPages: 0,
        currentUrl: url,
      };
      this.emit('progress', extractionProgress);

      extracted = await this.claudeClient.extractContent(conversion.markdown, extractInstruction);
    }

    return {
      url,
      ...(conversion.title !== undefined && { title: conversion.title }),
      markdown: conversion.markdown,
      ...(extracted !== undefined && { extracted }),
    };
  }

  /**
   * Fetch HTML content from a URL
   */
  private async fetchHtml(url: string, useHeadless: boolean = true): Promise<string> {
    const startTime = Date.now();
    logger.debug({ url, useHeadless }, 'Fetching HTML');

    if (useHeadless) {
      try {
        const result = await fetchWithPlaywright(url);
        const durationMs = Date.now() - startTime;
        logger.info(
          {
            url,
            useHeadless: true,
            durationMs,
            ...summarizePayload(result.html, 'raw-html', url),
          },
          'Raw HTML fetched'
        );
        return result.html;
      } catch (error) {
        // Wrap with distinctive message for error categorization
        throw new Error(
          `Headless fetch failed: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    }

    // Original axios implementation for static sites
    try {
      const response = await axios.get<string>(url, {
        timeout: this.config.timeout ?? DEFAULT_TIMEOUT,
        headers: {
          'User-Agent': this.config.userAgent ?? DEFAULT_USER_AGENT,
        },
      });

      const durationMs = Date.now() - startTime;
      logger.info(
        {
          url,
          useHeadless: false,
          durationMs,
          ...summarizePayload(response.data, 'raw-html', url),
        },
        'Raw HTML fetched'
      );

      return response.data;
    } catch (error) {
      logger.error(
        { url, error: error instanceof Error ? error.message : String(error) },
        'Failed to fetch HTML'
      );
      throw new Error(
        `Failed to fetch ${url}: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Stop the crawler
   */
  async stop(): Promise<void> {
    this.stopped = true;
    await closeBrowser();
  }
}
